import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BarChart3, Check, Database, Newspaper, Radio, Send, ShieldCheck, Trophy, Users, X } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { PATHS, fmtDay, useLang, useT } from "../../lib/i18n";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { TipCard, TipGrid } from "./TipCard";
import { Faq, RecordChart, Section, Stat } from "./Blocks";
import { BookieList } from "../../pages/VipPage";

const W = ({ children, className = "" }) => <div className={`mx-auto max-w-7xl px-4 sm:px-6 ${className}`}>{children}</div>;
function Below({ children }) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const id = (window.requestIdleCallback || ((f) => setTimeout(f, 200)))(() => setShow(true));
    return () => (window.cancelIdleCallback || clearTimeout)(id);
  }, []);
  return show ? <div className="fade-in">{children}</div> : <div style={{ minHeight: 1200 }} />;
}
const sign = (v) => `${v > 0 ? "+" : ""}${v}`;

function TrustRow({ record, gate }) {
  const t = useT();
  const r = record?.total;
  if (!r || !r.tips) return null;
  return (
    <div className="rise rise-4 mt-10" data-testid="trust-row">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label={t("Tipps gesamt", "Total tips")} value={r.tips} testid="trust-tips" />
        <Stat label={t("Profit in Units", "Profit in units")} value={`${sign(r.profit)}u`} accent testid="trust-profit" />
        <Stat label="Yield" value={`${r.yield}%`} testid="trust-yield" />
        <Stat label={t("Trefferquote", "Hit rate")} value={`${r.hit_rate}%`} testid="trust-hitrate" />
      </div>
    </div>
  );
}

function Hero({ p }) {
  const lang = useLang();
  const t = useT();
  const d = p.data;
  return (
    <section className="relative overflow-hidden border-b border-border">
      <div className="court-lines absolute inset-0" />
      <div className="absolute -right-24 -top-24 h-[28rem] w-[28rem] rounded-full bg-[var(--ball)]/10 blur-3xl" />
      <W className="relative pb-14 pt-14 lg:pt-20">
        <p className="rise text-xs font-semibold uppercase tracking-[0.22em] text-[var(--ball)]">{t("ATP · WTA · Challenger · Grand Slams", "ATP · WTA · Challenger · Grand Slams")}</p>
        <h1 className="rise rise-2 mt-3 max-w-4xl font-display text-5xl font-extrabold leading-[0.95] sm:text-6xl lg:text-7xl" data-testid="page-h1">{d.page.h1}</h1>
        <p className="rise rise-3 mt-5 max-w-2xl text-base text-muted-foreground md:text-lg" data-testid="hero-subline">{d.page.lead}</p>
        <div className="rise rise-4 mt-7 flex flex-wrap gap-3">
          <Link to={PATHS[lang].register} data-testid="hero-register-btn"><Button size="lg" className="rounded-full px-7 font-semibold">{t("Kostenlos registrieren & weitere Tipps sehen", "Join free & unlock more tips")} <ArrowRight size={16} className="ml-1" /></Button></Link>
          <a href="#gratis-tipp"><Button size="lg" variant="secondary" className="rounded-full px-7" data-testid="hero-free-tip-btn">{t("Gratis-Tipp ansehen", "View free tip")}</Button></a>
        </div>
        <TrustRow record={d.record} gate={d.stats_gate} />
      </W>
    </section>
  );
}

function FreeTip({ tip, tg }) {
  const t = useT();
  return (
    <Section title={t("Gratis-Tipp des Tages", "Free tip of the day")} testid="free-tip-section">
      <div id="gratis-tipp" className="scroll-mt-24 grid gap-6 lg:grid-cols-[1.4fr_1fr] lg:items-start">
        {tip ? <TipCard tip={tip} /> : (
          <div className="surface p-8" data-testid="free-tip-empty">
            <p className="font-display text-2xl font-bold">{t("Der Gratis-Tipp erscheint täglich bis ca. 11 Uhr", "The free tip is published daily by around 11 am (CET)")}</p>
            <p className="mt-2 text-sm text-muted-foreground">{t("Folge unserem Telegram-Kanal, dann verpasst du ihn nicht.", "Follow our Telegram channel so you never miss it.")}</p>
          </div>
        )}
        <TelegramMini url={tg} />
      </div>
    </Section>
  );
}

function TelegramMini({ url }) {
  const t = useT();
  return (
    <div className="surface p-6" data-testid="free-tip-telegram">
      <Send className="text-[var(--ball)]" size={22} />
      <p className="mt-3 font-semibold">{t("Gratis-Tipp direkt aufs Handy", "Free tip straight to your phone")}</p>
      <p className="mt-1 text-sm text-muted-foreground">{t("Im kostenlosen Telegram-Kanal posten wir jeden Gratis-Tipp und die Ergebnisse.", "Our free Telegram channel posts every free tip and its result.")}</p>
      {url ? <a href={url} target="_blank" rel="noopener noreferrer"><Button size="sm" className="mt-4 rounded-full" data-testid="free-tip-telegram-btn">{t("Kanal öffnen", "Open channel")}</Button></a>
        : <p className="mt-4 text-xs text-muted-foreground" data-testid="telegram-channel-soon">{t("Kanal-Link folgt in Kürze.", "Channel link coming soon.")}</p>}
    </div>
  );
}

function TodayLive({ h }) {
  const t = useT();
  return (
    <Section title={t("Heute & Live", "Today & live")} sub={t("VIP-Tipps sind verschwommen – mit VIP siehst du Pick, Quote und Begründung.", "VIP tips are blurred – unlock pick, odds and reasoning with VIP.")} testid="today-live-section">
      <TipGrid tips={h.today} empty={t("Für heute sind noch keine Tipps veröffentlicht.", "No tips published for today yet.")} />
      {h.live.length > 0 && (
        <div className="surface mt-6 divide-y divide-border" data-testid="live-scores">
          <p className="flex items-center gap-2 px-4 py-3 text-sm font-semibold"><Radio size={14} className="live-dot text-[var(--win)]" />Live</p>
          {h.live.map((e) => (
            <div key={e.id} className="flex flex-wrap items-center gap-3 px-4 py-3 text-sm">
              <span className="w-40 truncate text-xs text-muted-foreground">{e.league}</span>
              <span className="flex-1 font-medium">{e.home} <span className="text-muted-foreground">vs</span> {e.away}</span>
              <span className="font-num font-bold text-[var(--win)]">{e.ss}</span>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

function Countdown({ gs }) {
  const t = useT();
  const lang = useLang();
  const days = Math.max(0, Math.ceil((new Date(gs.start_date) - new Date()) / 86400000));
  const running = new Date(gs.start_date) <= new Date();
  return (
    <div className="relative overflow-hidden rounded-3xl border border-[var(--ball)]/30 bg-[radial-gradient(ellipse_at_top_left,rgba(215,255,58,.12),transparent_60%)] p-6 md:p-8" data-testid="gs-countdown">
      <Trophy className="text-[var(--ball)]" size={26} />
      <p className="mt-3 text-xs uppercase tracking-[0.18em] text-muted-foreground">{t("Nächstes Grand Slam", "Next Grand Slam")}</p>
      <Link to={gs.url} className="mt-1 block font-display text-4xl font-extrabold hover:text-[var(--ball)]">{gs.name}</Link>
      <p className="mt-1 text-sm text-muted-foreground">{gs.city} · {fmtDay(gs.start_date, lang)}</p>
      <p className="mt-5 font-num text-5xl font-bold" data-testid="gs-countdown-days">{running ? "LIVE" : days}<span className="ml-2 text-base font-normal text-muted-foreground">{running ? "" : t("Tage", "days")}</span></p>
      {gs.on_sale ? <Link to={PATHS[lang].vip}><Button className="mt-5 rounded-full" data-testid="gs-pass-btn">{t("Grand Slam Pass sichern", "Get the Grand Slam Pass")}</Button></Link>
        : <p className="mt-5 text-sm text-muted-foreground" data-testid="gs-pass-soon">{t("Der Grand Slam Pass ist bald erhältlich – alle Grand Slams sind im Jahrespaket enthalten.", "The Grand Slam Pass is coming soon – all Grand Slams are included in the annual package.")}</p>}
    </div>
  );
}

function Tournaments({ h }) {
  const t = useT();
  const lang = useLang();
  return (
    <Section title={t("Aktuelle Turniere", "Current tournaments")} sub={h.tournaments_upcoming ? t("Diese Woche läuft nichts – die nächsten Turniere:", "Nothing running this week – up next:") : t("Turniere dieser Woche", "Tournaments this week")} testid="tournaments-section">
      <div className="grid gap-6 lg:grid-cols-[1.6fr_1fr]">
        <div className="grid gap-3 sm:grid-cols-2">
          {h.tournaments.map((x) => (
            <Link key={x.id} to={x.url} className="surface surface-hover block p-5" data-testid={`home-tournament-${x.tour}-${x.slug}`}>
              <p className="text-xs uppercase tracking-wider text-[var(--ball)]">{x.category}</p>
              <p className="mt-1 font-display text-2xl font-bold leading-tight">{x.name}</p>
              <p className="mt-2 text-sm text-muted-foreground">{[x.city, x.surface, fmtDay(x.start_date, lang)].filter(Boolean).join(" · ")}</p>
            </Link>
          ))}
          {!h.tournaments.length && <p className="surface p-6 text-sm text-muted-foreground">{t("Turniere folgen.", "Tournaments coming soon.")}</p>}
        </div>
        {h.next_gs && <Countdown gs={{ ...h.next_gs, on_sale: !!h.gs_on_sale }} />}
      </div>
    </Section>
  );
}

const STEPS = [[Database, "Datenanalyse", "Data analysis", "Form, Belagsbilanz, Aufschlag- und Returnwerte, Head-to-Head.", "Form, surface record, serve and return numbers, head-to-head."],
  [BarChart3, "Quotenvergleich", "Odds comparison", "Wir vergleichen Anbieter und spielen nur Quoten mit Value.", "We compare bookmakers and only play prices with value."],
  [Newspaper, "News & Form", "News & form", "Verletzungen, Belastung, Reisen, Bedingungen vor Ort.", "Injuries, workload, travel and local conditions."],
  [ShieldCheck, "Freigabe durch unser Team", "Team approval", "Kein Tipp geht ohne manuelle Prüfung raus.", "No tip goes out without manual review."]];

function HowWeWork() {
  const t = useT();
  const lang = useLang();
  return (
    <Section title={t("So arbeiten wir", "How we work")} testid="how-we-work">
      <div className="grid gap-4 md:grid-cols-4">
        {STEPS.map(([I, de, en, dde, den], i) => (
          <div key={de} className="surface p-6" data-testid={`step-${i + 1}`}>
            <span className="font-num text-xs text-muted-foreground">0{i + 1}</span>
            <I className="mt-3 text-[var(--ball)]" size={24} />
            <p className="mt-3 font-display text-xl font-bold">{t(de, en)}</p>
            <p className="mt-1 text-sm text-muted-foreground">{t(dde, den)}</p>
          </div>
        ))}
      </div>
      <div className="surface mt-4 grid gap-4 p-6 md:grid-cols-[1fr_2fr] md:items-center" data-testid="units-explained">
        <p className="font-display text-2xl font-bold">{t("Units & Einsatz", "Units & stake")}</p>
        <p className="text-sm text-muted-foreground">{t("Jeder Tipp hat einen Einsatz von 1 bis 5 Units. Eine Unit ist deine feste Einsatzeinheit – meist 1 % deiner Wettkasse. So bleiben Ergebnisse vergleichbar und deine Bankroll geschützt.", "Every tip has a stake of 1 to 5 units. A unit is your fixed stake size – usually 1% of your bankroll. That keeps results comparable and your bankroll protected.")} <Link to={`/${lang}/${lang === "de" ? "ratgeber/units-bankroll-management" : "guides/units-bankroll-management"}/`} className="text-[var(--ball)] underline">{t("Mehr erfahren", "Learn more")}</Link></p>
      </div>
    </Section>
  );
}

function RecordTeaser({ d }) {
  const t = useT();
  const lang = useLang();
  if (!d.record && !d.home.recent.length) return null;
  return (
    <Section title={t("Ergebnisse", "Results")} testid="record-teaser" action={d.record && <Link to={PATHS[lang].record} className="text-sm text-[var(--ball)]" data-testid="record-full-link">{t("Komplette Bilanz", "Full record")} →</Link>}>
      {d.record && <RecordChart series={d.record.series} />}
      {d.home.recent.length > 0 && <><p className="mb-3 mt-8 text-sm font-semibold">{t("Zuletzt ausgewertet", "Recently settled")}</p><TipGrid tips={d.home.recent} /></>}
    </Section>
  );
}

const FEATS = [["Alle Tipps ATP, WTA, Challenger, WTA 125, ITF", "All ATP, WTA, Challenger, WTA 125, ITF tips", 1, 0, 1], ["Grand-Slam-Tipps inkl. Qualifikation", "Grand Slam tips incl. qualifying", 0, 1, 1],
  ["Website, E-Mail & Telegram", "Website, email & Telegram", 1, 1, 1], ["SMS-Benachrichtigung (Option)", "SMS alerts (option)", 1, 1, 1], ["Live-Scores, Quoten & H2H", "Live scores, odds & H2H", 1, 1, 1], ["Monatlich kündbar", "Cancel monthly", 1, 0, 0]];

function Pricing({ d }) {
  const t = useT();
  const lang = useLang();
  const pr = d.products;
  const [sms, setSms] = useState(false);
  const end = d.home.annual_end.split("-").reverse().join(".");
  const items = [["monthly", t("Monatlich", "Monthly"), pr.monthly, t("/Monat", "/month")], ["grand_slam", "Grand Slam Pass", pr.grand_slam, t(" einmalig", " one-time")],
    ["annual", t("Jahrespaket", "Annual"), pr.annual, t(" einmalig", " one-time")]];
  const desc = { monthly: t("Alle Tipps außer Grand Slams", "All tips except Grand Slams"), grand_slam: t("Alle Tipps zu einem Grand Slam", "All tips for one Grand Slam"), annual: t(`Alle Tipps bis ${end}`, `All tips until ${end}`) };
  return (
    <Section title={t("Pakete & Preise", "Plans & pricing")} sub={t("Endpreise inkl. MwSt.", "Final prices incl. VAT")} testid="pricing-section"
      action={<label className="flex items-center gap-2 text-sm">SMS <Switch checked={sms} onCheckedChange={setSms} data-testid="pricing-sms-toggle" /></label>}>
      <div className="grid gap-4 lg:grid-cols-3">
        {items.map(([k, n, p, note]) => (
          <div key={k} className={`surface flex flex-col p-6 ${k === "annual" ? "border-[var(--ball)]/60" : ""}`} data-testid={`home-product-${k}`}>
            <p className="font-display text-2xl font-bold">{n}</p>
            <p className="mt-1 text-sm text-muted-foreground">{desc[k]}</p>
            <p className="mt-5 font-num text-5xl font-bold" data-testid={`home-price-${k}`}>{sms ? p.price_sms : p.price}€<span className="text-sm font-normal text-muted-foreground">{note}</span></p>
            {k === "grand_slam" && !d.home.gs_on_sale ? <p className="mt-auto pt-5 text-center text-sm text-muted-foreground" data-testid="home-gs-soon">{t("Bald erhältlich", "Coming soon")}</p>
              : <Link to={`${PATHS[lang].vip}?product=${k}&sms=${sms ? 1 : 0}`} className="mt-auto pt-5"><Button className="w-full rounded-full" data-testid={`home-buy-${k}`}>{t("Auswählen", "Choose")}</Button></Link>}
          </div>
        ))}
      </div>
      <div className="surface mt-4 overflow-x-auto" data-testid="feature-table">
        <table className="w-full text-xs sm:text-sm">
          <thead className="text-left text-[10px] uppercase tracking-wider text-muted-foreground sm:text-xs"><tr><th className="px-2 py-3 sm:px-4" />{items.map(([k, n]) => <th key={k} className="px-1 py-3 text-center sm:px-4">{n}</th>)}</tr></thead>
          <tbody>{FEATS.map(([de, en, ...v]) => <tr key={de} className="border-t border-border"><td className="px-2 py-3 sm:px-4">{t(de, en)}</td>{v.map((x, i) => <td key={i} className="px-1 py-3 text-center sm:px-4">{x ? <Check size={16} className="mx-auto text-[var(--ball)]" /> : <X size={16} className="mx-auto text-muted-foreground" />}</td>)}</tr>)}</tbody>
        </table>
      </div>
    </Section>
  );
}

function FreeVip({ d }) {
  const t = useT();
  const { user, config } = useAuth();
  const [country, setCountry] = useState(user?.country || "");
  useEffect(() => { if (!country) api.get("/geo").then((r) => r.data.country && setCountry(r.data.country)).catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <section className="mt-16 grid gap-8 rounded-3xl border border-border bg-card p-6 md:p-10 lg:grid-cols-2" data-testid="home-free-vip">
      <div>
        <Users className="text-[var(--ball)]" size={28} />
        <h2 className="mt-3 font-display text-4xl font-extrabold">{t("VIP kostenlos über Wettanbieter", "Free VIP via bookmaker")}</h2>
        <p className="mt-2 text-muted-foreground">{t(`Registriere dich über unseren Link bei einem Partner-Buchmacher in deinem Land, zahle mind. ${config?.bookie_min_deposit || 100} € ein und lade einen Screenshot hoch. Nach Prüfung erhältst du VIP bis ${d.home.bookie_vip_end.split("-").reverse().join(".")}.`, `Sign up with a partner bookmaker in your country via our link, deposit at least €${config?.bookie_min_deposit || 100} and upload a screenshot. After review you get VIP until ${d.home.bookie_vip_end}.`)}</p>
      </div>
      <BookieList country={country} setCountry={setCountry} />
    </section>
  );
}

function Telegram({ url }) {
  const t = useT();
  const lang = useLang();
  return (
    <Section title="Telegram" testid="telegram-section">
      <div className="grid gap-4 md:grid-cols-2">
        <div className="surface p-6"><p className="font-display text-2xl font-bold">{t("Kostenloser Kanal", "Free channel")}</p>
          <p className="mt-2 text-sm text-muted-foreground">{t("Täglicher Gratis-Tipp, Ergebnisse und Turnier-Infos.", "Daily free tip, results and tournament news.")}</p>
          {url ? <a href={url} target="_blank" rel="noopener noreferrer"><Button className="mt-4 rounded-full" data-testid="telegram-channel-btn">{t("Kanal beitreten", "Join channel")}</Button></a> : <p className="mt-4 text-xs text-muted-foreground">{t("Kanal-Link folgt in Kürze.", "Channel link coming soon.")}</p>}
        </div>
        <div className="surface p-6"><p className="font-display text-2xl font-bold">{t("VIP-Bot", "VIP bot")}</p>
          <ul className="mt-2 space-y-1.5 text-sm text-muted-foreground">{[t("Alle VIP-Tipps sofort als Nachricht", "Every VIP tip instantly as a message"), t("Abo direkt im Bot kaufen", "Buy your plan inside the bot"), t("Buchmacher-Screenshot hochladen", "Upload bookmaker screenshot"), t("Konto mit einem Klick verknüpfen", "Link your account in one click")].map((x) => <li key={x} className="flex gap-2"><Check size={15} className="mt-0.5 text-[var(--ball)]" />{x}</li>)}</ul>
          <Link to={PATHS[lang].account}><Button variant="secondary" className="mt-4 rounded-full" data-testid="telegram-bot-link-btn">{t("Im Konto verknüpfen", "Link in account")}</Button></Link>
        </div>
      </div>
    </Section>
  );
}

function Guides({ list }) {
  const t = useT();
  const lang = useLang();
  const base = `/${lang}/${lang === "de" ? "ratgeber" : "guides"}/`;
  if (!list.length) return null;
  return (
    <Section title={t("Ratgeber", "Guides")} testid="guides-teaser" action={<Link to={base} className="text-sm text-[var(--ball)]" data-testid="guides-all-link">{t("Alle Ratgeber", "All guides")} →</Link>}>
      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        {list.map((g) => (
          <Link key={g.id} to={`${base}${g.slug}/`} className="surface surface-hover block p-5" data-testid={`guide-teaser-${g.slug}`}>
            <p className="font-display text-xl font-bold leading-tight">{g.title}</p>
            <p className="mt-2 line-clamp-3 text-sm text-muted-foreground">{g.excerpt}</p>
          </Link>
        ))}
      </div>
    </Section>
  );
}

export default function HomePage({ p }) {
  const d = p.data;
  const h = d.home;
  return (
    <>
      <Hero p={p} />
      <W className="py-4">
        <FreeTip tip={d.free_tips[0]} tg={h.telegram_channel_url} />
        <TodayLive h={h} />
        <Below>
          <Tournaments h={h} />
          <HowWeWork />
          <RecordTeaser d={d} />
          <Pricing d={d} />
          <FreeVip d={d} />
          <Telegram url={h.telegram_channel_url} />
          <Guides list={h.guides} />
          <Faq items={p.faq} />
          {d.page.seo_html && <section className="mt-20 max-w-3xl"><div className="prose-tt" data-testid="home-seo-text" dangerouslySetInnerHTML={{ __html: d.page.seo_html }} /></section>}
        </Below>
      </W>
    </>
  );
}
